
import { defineComponent, ref, computed, onUnmounted } from "vue";

export default defineComponent({
  name: "VideoCustom",
  props: {
    src: { type: String, required: true },
    allowSeek: { type: Boolean, default: false },
  },
  emits: ["ended", "pause", "timeupdate"],
  setup(props, { emit, expose }) {
    const videoEl = ref<HTMLVideoElement | null>(null);
    const isPlaying = ref(false);
    const isBuffering = ref(false);
    const isMuted = ref(false);
    const volume = ref(1);
    const currentTime = ref(0);
    const duration = ref(0);
    const maxReached = ref(0);
    const showControls = ref(true);
    let hideTimer: ReturnType<typeof setTimeout> | null = null;
    let lastProgressEmit = -10;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

    const onIOSFullscreenBlock = () => {
      if (videoEl.value) {
        videoEl.value.pause();
        (videoEl.value as any).webkitExitFullscreen?.();
        videoEl.value.play();
      }
    };

    const progressPct = computed(() =>
      duration.value > 0 ? (currentTime.value / duration.value) * 100 : 0
    );

    const formatTime = (s: number) => {
      const m = Math.floor(s / 60);
      const sec = Math.floor(s % 60);
      return `${m}:${sec.toString().padStart(2, "0")}`;
    };

    const onLoaded = () => {
      if (videoEl.value) duration.value = videoEl.value.duration;
    };

    const onTimeUpdate = () => {
      if (!videoEl.value) return;
      const t = videoEl.value.currentTime;
      if (!props.allowSeek && t > maxReached.value + 1) {
        videoEl.value.currentTime = maxReached.value;
        return;
      }
      if (t > maxReached.value) maxReached.value = t;
      currentTime.value = t;
      if (t - lastProgressEmit >= 10) {
        lastProgressEmit = t;
        emit("timeupdate", t);
      }
    };

    const onEnded = () => {
      isPlaying.value = false;
      emit("ended");
    };

    const onPause = () => {
      isPlaying.value = false;
      emit("pause");
    };

    const startHideTimer = () => {
      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = setTimeout(() => {
        if (isPlaying.value) showControls.value = false;
      }, 3000);
    };

    const togglePlay = () => {
      if (!videoEl.value) return;
      if (videoEl.value.paused) {
        videoEl.value.play();
        isPlaying.value = true;
        startHideTimer();
      } else {
        videoEl.value.pause();
        isPlaying.value = false;
        showControls.value = true;
      }
    };

    const toggleMute = () => {
      if (!videoEl.value) return;
      isMuted.value = !isMuted.value;
      videoEl.value.muted = isMuted.value;
    };

    const onVolumeInput = (e: Event) => {
      const v = parseFloat((e.target as HTMLInputElement).value);
      volume.value = v;
      if (videoEl.value) videoEl.value.volume = v;
      isMuted.value = v === 0;
    };

    const toggleFullscreen = () => {
      const el = videoEl.value?.parentElement;
      if (!el) return;
      if (!document.fullscreenElement) {
        el.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    };

    const pause = () => videoEl.value?.pause();
    const play = () => videoEl.value?.play();
    const load = () => videoEl.value?.load();
    const resetMax = () => { maxReached.value = 0; };
    const seekTo = (time: number) => {
      if (!videoEl.value) return;
      maxReached.value = time;
      videoEl.value.currentTime = time;
    };

    // Expose native-video-like API so parent can use videoRef.value.pause() etc.
    expose({
      get src() { return videoEl.value?.src ?? ""; },
      set src(v: string) { if (videoEl.value) videoEl.value.src = v; },
      get duration() { return videoEl.value?.duration ?? 0; },
      get currentTime() { return videoEl.value?.currentTime ?? 0; },
      set currentTime(v: number) { if (videoEl.value) videoEl.value.currentTime = v; },
      get paused() { return videoEl.value?.paused ?? true; },
      get ended() { return videoEl.value?.ended ?? false; },
      pause,
      play,
      load,
      resetMax,
      seekTo,
      addEventListener: (type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions) => {
        videoEl.value?.addEventListener(type, listener, options);
      },
      removeEventListener: (type: string, listener: EventListenerOrEventListenerObject) => {
        videoEl.value?.removeEventListener(type, listener);
      },
    });

    onUnmounted(() => {
      if (hideTimer) clearTimeout(hideTimer);
      if (videoEl.value) {
        videoEl.value.pause();
        videoEl.value.src = "";
        videoEl.value.load();
      }
    });

    return {
      videoEl,
      isPlaying,
      isBuffering,
      isMuted,
      volume,
      currentTime,
      duration,
      showControls,
      progressPct,
      formatTime,
      onLoaded,
      onTimeUpdate,
      onEnded,
      onPause,
      togglePlay,
      toggleMute,
      onVolumeInput,
      toggleFullscreen,
      startHideTimer,
      pause,
      resetMax,
      isIOS,
      onIOSFullscreenBlock,
    };
  },
});
